import { clientAPI } from "@/lib/client-fetch"
import { snakeToCamel } from "@/lib/utils"
import API_ENDPOINTS from "@/constants/api-endpoints"
import { useCartStore } from "./cart-store"
import type { RefreshCartResponse } from "./types"
import { SELECTION_UNIT_PIECE } from "@/constants/misc"

export async function refreshCart(microhubId: string | null): Promise<RefreshCartResponse | null> {
  const { entries, outOfStockEntries, setValidatedItems, setOutOfStockValidatedItems, setIsRefreshing, setLastRefreshedAt, reconcileStock } =
    useCartStore.getState()

  const allItems = [...entries, ...outOfStockEntries]

  if (allItems.length === 0) {
    setValidatedItems([])
    setOutOfStockValidatedItems([])
    return { items: [], unavailableItems: [] }
  }

  setIsRefreshing(true)

  const body = {
    items: allItems.map((e) => ({ product_id: e.productId, quantity: e.quantity, unit: e.unit })),
    microhub_id: microhubId,
  }

  const result = await clientAPI.post(API_ENDPOINTS.REFRESH_CART, body)

  setIsRefreshing(false)

  if (!result.ok) return null

  const data = snakeToCamel(result.data) as RefreshCartResponse

  // Build a lookup from productId → local entry fields
  const entryMap = new Map(allItems.map((e) => [e.productId, e]))

  // Coerce productIds to strings and merge local fields not echoed by server
  const mergeLocalFields = (i: any) => {
    const pid = String(i.productId)
    const local = entryMap.get(pid)
    return {
      ...i,
      productId: pid,
      unit: i.unit || local?.unit || SELECTION_UNIT_PIECE,
      priceMeasurementUnit: i.priceMeasurementUnit || local?.priceMeasurementUnit || "",
      canBePurchasedByUnit: i.canBePurchasedByUnit ?? local?.canBePurchasedByUnit ?? true,
      canBePurchasedByKg: i.canBePurchasedByKg ?? local?.canBePurchasedByKg ?? false,
    }
  }

  const items = data.items.map(mergeLocalFields)
  const unavailableItems = (data.unavailableItems ?? []).map(mergeLocalFields)

  const availableIds = items.map((i) => i.productId)
  const unavailableIds = unavailableItems.map((i) => i.productId)
  reconcileStock(availableIds, unavailableIds)

  setValidatedItems(items)
  setOutOfStockValidatedItems(unavailableItems)
  setLastRefreshedAt(Date.now())

  return { items, unavailableItems }
}
